import axios, { AxiosInstance } from 'axios';
import { resolveReactAppApiBaseUrl } from '../utils/reactAppApiBaseUrl';
import {
  DATABASE_CONNECTION_ERROR_EVENT,
  SESSION_EXPIRED_EVENT,
  apiErrorToastMeta,
  databaseConnectionToastMessage,
  dispatchApiErrorToast,
  isDatabaseConnectionApiError,
  isUnauthorizedApiError,
  unauthorizedToastMessage,
} from '../utils/httpClientErrorMessage';

/** Must match AuthContext storage key for POST /api/auth/refresh */
export const REFRESH_TOKEN_KEY = 'refresh_token';

export function resetSessionExpirySignal() {
  sessionExpirySignaled = false;
}

type RetryCfg = { __ffRetryAttempt?: number; __ffAuthRetry?: boolean };

let sessionExpirySignaled = false;

function signalSessionExpired() {
  if (typeof window === 'undefined' || sessionExpirySignaled) return;
  sessionExpirySignaled = true;
  try {
    localStorage.removeItem('token');
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem('user');
  } catch {
    // ignore
  }
  delete api.defaults.headers.common['Authorization'];
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

// Configure axios instance
export const api: AxiosInstance = axios.create({
  baseURL: resolveReactAppApiBaseUrl(),
  timeout: 90000, // generous timeout for heavy review/sync calls
});

// Add Authorization header from localStorage if available
api.interceptors.request.use(
  (config) => {
    // Resolve at request time so the first module evaluation (rare edge cases) never pins a wrong host.
    config.baseURL = resolveReactAppApiBaseUrl();
    // Always check localStorage for token (in case AuthContext hasn't set it yet)
    const token = localStorage.getItem('token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    console.log('API Request:', config.method?.toUpperCase(), config.url);
    if (token) {
      console.log('  Authorization header:', config.headers.Authorization ? 'Present' : 'Missing');
    }
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling + short retries on transient proxy/API failures
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config as (typeof error.config & RetryCfg) | undefined;

    // Access-token expiry: refresh using long-lived refresh_token, then retry once (e.g. long rule-sync polls).
    if (
      config &&
      error.response?.status === 401 &&
      !config.__ffAuthRetry &&
      typeof config.url === 'string'
    ) {
      const url = config.url || '';
      const isAuthLoginRegister =
        url.includes('/api/auth/login') || url.includes('/api/auth/register');
      const isRefreshCall = url.includes('/api/auth/refresh');
      const refreshTok = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!isAuthLoginRegister && !isRefreshCall && refreshTok) {
        try {
          const base = resolveReactAppApiBaseUrl();
          const { data } = await axios.post<{ token?: string }>(
            `${base}/api/auth/refresh`,
            {},
            {
              headers: { Authorization: `Bearer ${refreshTok}` },
              timeout: 90000,
            }
          );
          const newAccess = data?.token;
          if (newAccess) {
            localStorage.setItem('token', newAccess);
            api.defaults.headers.common['Authorization'] = `Bearer ${newAccess}`;
            config.headers = config.headers ?? {};
            (config.headers as Record<string, string>).Authorization = `Bearer ${newAccess}`;
            config.__ffAuthRetry = true;
            return api.request(config);
          }
        } catch {
          signalSessionExpired();
        }
      }
    }

    if (config) {
      const status = error.response?.status;
      const url = String(config.url || '');
      const isMultipartUpload =
        (typeof FormData !== 'undefined' && config.data instanceof FormData) ||
        url.includes('/api/documents/upload');
      // Never auto-retry document uploads: timeout retries re-send the whole file
      // and make a slow Hetzner/S3 write look hung for minutes.
      const shouldRetry =
        !isMultipartUpload &&
        (status === 502 ||
          status === 503 ||
          error.code === 'ECONNABORTED' ||
          error.message === 'Network Error' ||
          error.code === 'ERR_NETWORK');
      const attempt = Number(config.__ffRetryAttempt || 0);
      // Up to 3 retries (4 total attempts) for cold hosts / flaky networks
      if (shouldRetry && attempt < 3) {
        config.__ffRetryAttempt = attempt + 1;
        const delayMs = 900 * 2 ** attempt;
        await new Promise((r) => setTimeout(r, delayMs));
        return api.request(config);
      }
    }

    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isAuthLoginRegister =
        url.includes('/api/auth/login') || url.includes('/api/auth/register');
      const isRefreshCall = url.includes('/api/auth/refresh');
      if (!isAuthLoginRegister && !isRefreshCall) {
        console.warn('Session expired (401):', url);
        signalSessionExpired();
      }
    }
    if (typeof window !== 'undefined' && isDatabaseConnectionApiError(error)) {
      const url = String(error.config?.url || '');
      if (!url.includes('/api/health')) {
        window.dispatchEvent(
          new CustomEvent(DATABASE_CONNECTION_ERROR_EVENT, {
            detail: {
              message: databaseConnectionToastMessage(error),
              ...apiErrorToastMeta(error),
            },
          })
        );
      }
    }
    if (typeof window !== 'undefined' && isUnauthorizedApiError(error)) {
      const url = String(error.config?.url || '');
      const skipToast =
        url.includes('/api/auth/login') ||
        url.includes('/api/auth/register') ||
        url.includes('/api/auth/refresh') ||
        url.includes('/api/health');
      if (!skipToast) {
        const text = unauthorizedToastMessage(error);
        dispatchApiErrorToast({
          title: text,
          message: text,
          ...apiErrorToastMeta(error),
        });
      }
    }
    // Don't auto-logout on 503/502/network errors - backend might be temporarily down
    // Components should handle these errors gracefully
    return Promise.reject(error);
  }
);
